// src/components/dashboards/EnvironmentFilterChips.tsx
// Compact, glass-morphic chip row for filtering by environment.
// Hidden automatically when the user has <= 1 environment.
//
// Behaviour:
//   - ≤ COLLAPSE_THRESHOLD selected → individual chips
//   - more selected                 → single summary chip ("X environments selected")
//   - One unified "+ Add" dropdown with checkboxes for ALL environments + search

import React, { useCallback, useMemo, useRef, useState } from "react";
import {
  alpha,
  Box,
  Checkbox,
  Chip,
  ClickAwayListener,
  Divider,
  Fade,
  InputAdornment,
  Paper,
  Popper,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DomainIcon from "@mui/icons-material/Domain";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import { useEnvironmentFilter } from "hooks/useEnvironmentFilter";
import { useTranslation } from "../../i18n";
import { athenaColors } from "../../theme/palette";
import { useDashboardInk } from "./dashboardInk";
import { glassInputSx } from "../../theme/glassTokens";
import { Environment } from "types";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** When more than this many environments are selected, show a summary chip */
const COLLAPSE_THRESHOLD = 4;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const EnvironmentFilterChips: React.FC = () => {
  const theme = useTheme();
  const ink = useDashboardInk();
  const { t } = useTranslation();
  const {
    allEnvironments,
    selectedIds,
    isFiltered,
    showFilter,
    toggle,
    selectAll,
    setSelection,
  } = useEnvironmentFilter();

  // Dropdown state
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const [search, setSearch] = useState("");

  // Derived
  const selected = useMemo(
    () => allEnvironments.filter((e) => selectedIds.includes(Number(e.id))),
    [allEnvironments, selectedIds],
  );

  const allSelected = !isFiltered;

  const shouldCollapse =
    selected.length > COLLAPSE_THRESHOLD ||
    allEnvironments.length > COLLAPSE_THRESHOLD + 2; // always collapse if many total

  // Filtered list for the search
  const filteredEnvs = useMemo(() => {
    if (!search.trim()) return allEnvironments;
    const q = search.toLowerCase();
    return allEnvironments.filter((e) => e.name.toLowerCase().includes(q));
  }, [allEnvironments, search]);

  // Group the filtered envs by consortium topology (Environment type, Joan 2026-05-27):
  // each consortium (or the parent of an institution env) becomes a header, its member
  // envs sit indented beneath it, and personal / un-parented envs sit ungrouped at the end.
  // Falls back to parent_consortium_env_name when the consortium env itself is not in the
  // list (a member institution can still render its group header).
  type EnvGroupItem =
    | { type: "header"; id: string; label: string }
    | { type: "env"; env: Environment; indented: boolean };
  const groupedEnvItems = useMemo<EnvGroupItem[]>(() => {
    const consortiumById = new Map<string, Environment>();
    const childrenByParent = new Map<string, Environment[]>();
    const standalone: Environment[] = [];
    for (const e of filteredEnvs) {
      if (e.kind === "consortium") {
        consortiumById.set(String(e.id), e);
        continue;
      }
      const pid =
        e.parent_consortium_env != null ? String(e.parent_consortium_env) : null;
      if (pid) {
        const arr = childrenByParent.get(pid) || [];
        arr.push(e);
        childrenByParent.set(pid, arr);
      } else {
        standalone.push(e);
      }
    }
    const parentIds = new Set<string>([
      ...consortiumById.keys(),
      ...childrenByParent.keys(),
    ]);
    const headers = Array.from(parentIds)
      .map((pid) => {
        const cEnv = consortiumById.get(pid);
        const firstChild = (childrenByParent.get(pid) || [])[0];
        const label =
          cEnv?.name ||
          firstChild?.parent_consortium_env_name ||
          t("dashboard.environment_filter.consortium_group", "Consortium");
        return { pid, label };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
    const items: EnvGroupItem[] = [];
    for (const { pid, label } of headers) {
      items.push({ type: "header", id: `h-${pid}`, label });
      const cEnv = consortiumById.get(pid);
      if (cEnv) items.push({ type: "env", env: cEnv, indented: true });
      for (const child of childrenByParent.get(pid) || []) {
        items.push({ type: "env", env: child, indented: true });
      }
    }
    for (const e of standalone) items.push({ type: "env", env: e, indented: false });
    return items;
  }, [filteredEnvs, t]);

  // Handlers
  const handleToggleDropdown = useCallback(() => {
    setDropdownOpen((prev) => {
      if (prev) setSearch(""); // reset search on close
      return !prev;
    });
  }, []);

  const handleCloseDropdown = useCallback(() => {
    setDropdownOpen(false);
    setSearch("");
  }, []);

  const handleToggleEnv = useCallback(
    (id: number) => {
      toggle(id);
    },
    [toggle],
  );

  const handleSelectAllToggle = useCallback(() => {
    if (allSelected) {
      // Deselect all → keep only the first (can't have zero)
      if (allEnvironments.length > 0) {
        setSelection([Number(allEnvironments[0].id)]);
      }
    } else {
      selectAll();
    }
  }, [allSelected, allEnvironments, selectAll, setSelection]);

  // Don't render if only 1 (or 0) environments
  if (!showFilter) return null;

  // ---- Shared chip style ----
  const selectedChipSx = {
    height: 28,
    fontSize: "0.78rem",
    fontWeight: 500,
    backdropFilter: "blur(6px)",
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
    color: ink.primary,
    "& .MuiChip-deleteIcon": {
      fontSize: 16,
      color: alpha(theme.palette.text.primary, 0.4),
      "&:hover": { color: theme.palette.error.main },
    },
    "& .MuiChip-icon": {
      color: ink.secondary,
    },
  };

  // ---- Summary chip label ----
  const summaryLabel = allSelected
    ? t("dashboard.environment_filter.all_envs", "All environments")
    : t("dashboard.environment_filter.n_selected", "{{count}} environments selected").replace(
        "{{count}}",
        String(selected.length),
      );

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        flexWrap: "wrap",
        py: 0.75,
        px: 0.5,
      }}
    >
      {/* Label */}
      <Typography
        variant="caption"
        sx={{
          fontWeight: 600,
          color: ink.secondary,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          fontSize: "0.68rem",
          mr: 0.5,
        }}
      >
        {t("dashboard.environment_filter.label", "Environments")}:
      </Typography>

      {/* ================================================================= */}
      {/* EXPANDED — individual chips per selected environment               */}
      {/* ================================================================= */}
      {!shouldCollapse &&
        selected.map((env) => (
          <Chip
            key={env.id}
            icon={<DomainIcon sx={{ fontSize: 14, opacity: 0.7 }} />}
            label={env.name}
            size="small"
            onDelete={() => toggle(Number(env.id))}
            sx={selectedChipSx}
          />
        ))}

      {/* ================================================================= */}
      {/* COLLAPSED — single summary chip                                   */}
      {/* ================================================================= */}
      {shouldCollapse && (
        <Chip
          icon={
            allSelected ? (
              <DomainIcon sx={{ fontSize: 14, opacity: 0.7 }} />
            ) : (
              <FilterListIcon sx={{ fontSize: 14, opacity: 0.7 }} />
            )
          }
          label={summaryLabel}
          size="small"
          onClick={handleToggleDropdown}
          onDelete={
            isFiltered
              ? () => {
                  selectAll();
                  setDropdownOpen(false);
                }
              : undefined
          }
          deleteIcon={
            isFiltered ? (
              <Tooltip title={t("dashboard.environment_filter.reset_to_all", "Reset to all")}>
                <CheckBoxIcon sx={{ fontSize: 16 }} />
              </Tooltip>
            ) : undefined
          }
          sx={{
            ...selectedChipSx,
            cursor: "pointer",
            ...(allSelected && {
              backgroundColor: alpha(athenaColors.royalBlue, 0.08),
              borderColor: alpha(athenaColors.royalBlue, 0.3),
            }),
          }}
        />
      )}

      {/* ================================================================= */}
      {/* "+ Add" chip — opens the unified dropdown                         */}
      {/* ================================================================= */}
      <Box ref={anchorRef} sx={{ display: "inline-flex" }}>
        <Chip
          icon={<AddIcon sx={{ fontSize: 16}} />}
          label={t("dashboard.environment_filter.add", "Add")}
          size="small"
          variant="outlined"
          onClick={handleToggleDropdown}
          sx={{
            height: 28,
            fontSize: "0.75rem",
            fontWeight: 500,
            cursor: "pointer",
            borderStyle: "dashed",
            borderColor: dropdownOpen
              ? athenaColors.royalBlue
              : alpha(theme.palette.divider, 0.4),
            color: dropdownOpen
              ? athenaColors.royalBlue
              : ink.secondary,
            "& .MuiChip-icon": {
              color: athenaColors.royalBlue, // ensure + icon uses royalBlue
            },
            "&:hover": {
              borderColor: athenaColors.royalBlue,
              color: athenaColors.royalBlue,
            },
          }}
        />
      </Box>

      {/* ================================================================= */}
      {/* DROPDOWN — unified, searchable, all environments with checkboxes  */}
      {/* ================================================================= */}
      <Popper
        open={dropdownOpen}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        transition
        sx={{ zIndex: (t) => t.zIndex.modal }}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={200}>
            <Paper
              elevation={8}
              sx={{
                mt: 0.5,
                borderRadius: 2,
                overflow: "hidden",
                minWidth: 280,
                maxWidth: 380,
                // This popover is a PORTAL, so it never inherited the dark
                // dashboard's ink: every label in it was deepNavy or royalBlue
                // on a translucent dark paper, i.e. an environment picker you
                // could not read. Scoped to the dashboard's dark variants
                // only; everywhere else the popover is unchanged.
                ...(ink.dark && {
                  backgroundColor: alpha(athenaColors.deepNavy, 0.96),
                  backgroundImage: "none",
                  border: `1px solid ${alpha(theme.palette.common.white, 0.14)}`,
                  color: theme.palette.common.white,
                  ...glassInputSx,
                  "& .MuiDivider-root": {
                    borderColor: alpha(theme.palette.common.white, 0.1),
                  },
                }),
              }}
            >
              <ClickAwayListener onClickAway={handleCloseDropdown}>
                <Box>
                  {/* Header */}
                  <Box
                    sx={{
                      px: 2,
                      pt: 1.5,
                      pb: 0.75,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        color: ink.dark
                          ? athenaColors.periwinkle
                          : athenaColors.royalBlue,
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                      }}
                    >
                      {t("dashboard.environment_filter.select_env", "SELECT ENVIRONMENT")}
                    </Typography>
                    <Typography
                      variant="caption"
                      onClick={handleSelectAllToggle}
                      sx={{
                        fontWeight: 500,
                        color: ink.dark
                          ? athenaColors.periwinkle
                          : athenaColors.royalBlue,
                        cursor: "pointer",
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      {allSelected
                        ? t("dashboard.environment_filter.deselect_all", "Deselect All")
                        : t("dashboard.environment_filter.select_all", "Select All")}
                    </Typography>
                  </Box>

                  {/* Search */}
                  <Box sx={{ px: 1.5, pb: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder={t(
                        "dashboard.environment_filter.search_placeholder",
                        "Search environments...",
                      )}
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      autoFocus
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon
                              sx={{
                                fontSize: 18,
                                color: ink.dark
                                  ? alpha(theme.palette.common.white, 0.55)
                                  : athenaColors.blueGray,
                              }}
                            />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          fontSize: "0.85rem",
                          borderRadius: 1.5,
                        },
                      }}
                    />
                  </Box>

                  <Divider />

                  {/* Environment list */}
                  <Box
                    sx={{
                      maxHeight: 260,
                      overflowY: "auto",
                      py: 0.5,
                    }}
                  >
                    {filteredEnvs.length === 0 ? (
                      <Typography
                        variant="body2"
                        sx={{
                          px: 2,
                          py: 1.5,
                          textAlign: "center",
                          color: ink.dark
                            ? alpha(theme.palette.common.white, 0.65)
                            : "text.secondary",
                        }}
                      >
                        {t("dashboard.environment_filter.no_results", "No environments found")}
                      </Typography>
                    ) : (
                      groupedEnvItems.map((item) => {
                        if (item.type === "header") {
                          return (
                            <Typography
                              key={item.id}
                              sx={{
                                px: 1.5,
                                pt: 1,
                                pb: 0.25,
                                fontSize: "0.7rem",
                                fontWeight: 700,
                                letterSpacing: 0.4,
                                textTransform: "uppercase",
                                color: ink.dark
                                  ? alpha(theme.palette.common.white, 0.5)
                                  : athenaColors.blueGray,
                              }}
                            >
                              {item.label}
                            </Typography>
                          );
                        }
                        const env = item.env;
                        const isChecked = selectedIds.includes(Number(env.id));
                        return (
                          <Box
                            key={env.id}
                            onClick={() => handleToggleEnv(Number(env.id))}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              pl: item.indented ? 3.5 : 1.5,
                              pr: 1.5,
                              py: 0.5,
                              cursor: "pointer",
                              borderRadius: 1,
                              mx: 0.5,
                              transition: "background-color 0.15s ease",
                              "&:hover": {
                                backgroundColor: ink.dark
                                  ? alpha(theme.palette.common.white, 0.08)
                                  : alpha(theme.palette.primary.main, 0.06),
                              },
                            }}
                          >
                            <Checkbox
                              checked={isChecked}
                              icon={
                                <CheckBoxOutlineBlankIcon
                                  sx={{
                                    fontSize: 18,
                                    color: ink.dark
                                      ? alpha(theme.palette.common.white, 0.45)
                                      : athenaColors.blueGray,
                                  }}
                                />
                              }
                              checkedIcon={
                                <CheckBoxIcon
                                  sx={{
                                    fontSize: 18,
                                    color: ink.dark
                                      ? athenaColors.periwinkle
                                      : athenaColors.royalBlue,
                                  }}
                                />
                              }
                              sx={{ p: 0 }}
                              disableRipple
                              tabIndex={-1}
                            />
                            <DomainIcon
                              sx={{
                                fontSize: 16,
                                color: isChecked
                                  ? ink.dark
                                    ? athenaColors.periwinkle
                                    : athenaColors.royalBlue
                                  : ink.dark
                                  ? alpha(theme.palette.common.white, 0.45)
                                  : athenaColors.blueGray,
                              }}
                            />
                            <Typography
                              sx={{
                                fontSize: "0.85rem",
                                fontWeight: isChecked ? 600 : 400,
                                flex: 1,
                              }}
                            >
                              {env.name}
                            </Typography>
                          </Box>
                        );
                      })
                    )}
                  </Box>

                  {/* Footer */}
                  <Divider />
                  <Box
                    sx={{
                      px: 2,
                      py: 1,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: ink.dark
                          ? alpha(theme.palette.common.white, 0.65)
                          : "text.secondary",
                      }}
                    >
                      {t(
                        "dashboard.environment_filter.count_footer",
                        "{{selected}} of {{total}} selected",
                      )
                        .replace("{{selected}}", String(selected.length))
                        .replace("{{total}}", String(allEnvironments.length))}
                    </Typography>
                  </Box>
                </Box>
              </ClickAwayListener>
            </Paper>
          </Fade>
        )}
      </Popper>
    </Box>
  );
};

export default EnvironmentFilterChips;
